import { t } from '../i18n';
import { isValidE164Phone, normalizeToE164 } from '../crypto/smsPassword';

/** API field path → HTML form control `name`. */
export const SETTINGS_FIELD_TO_CONTROL: Record<string, string> = {
  'guestVerifyLockout.failureWindowMinutes': 'failureWindowMinutes',
  'guestVerifyLockout.maxFailuresPerLink': 'maxFailuresPerLink',
  'guestVerifyLockout.lockoutMinutes': 'lockoutMinutes',
  'guestVerifyLockout.maxRequestsPerIp': 'maxRequestsPerIp',
  'fileSharing.maxFolderExtensionDays': 'maxFolderExtensionDays',
  'fileSharing.maxFolderLifetimeDays': 'maxFolderLifetimeDays',
  'fileSharing.fileDeleteRetentionDays': 'fileDeleteRetentionDays',
  'fileSharing.folderExpiryWarningDays': 'folderExpiryWarningDays',
  'fileSharing.folderExpiryWarningSendTimeUtc': 'folderExpiryWarningSendTimeUtc',
  'informationSharing.incompleteDownloadFallbackSeconds': 'incompleteDownloadFallbackDays',
  'informationSharing.unopenedReplyRetentionDays': 'unopenedReplyRetentionDays',
  'storage.deploymentWarningBytes': 'deploymentWarningGiB',
  'guestSession.idleTtlMinutes': 'guestIdleTtlMinutes',
  'guestSession.maxLifetimeHours': 'guestMaxLifetimeHours',
  'upload.sessionIdleTtlHours': 'uploadSessionIdleTtlHours',
  'cmComBudget.logicalAccountId': 'cmComLogicalAccountId',
  'cmComBudget.creditValidityDays': 'cmComCreditValidityDays',
  'cmComBudget.lowBalanceThreshold': 'cmComLowBalanceThreshold',
  'cmComBudget.expiryWarningDays': 'cmComExpiryWarningDays',
  'cmComBudget.pollSendTimeUtc': 'cmComPollSendTimeUtc',
  'cmComBudget.opsPhoneNumbers': 'cmComOpsPhoneNumbers',
  'cmComBudget.seededLastTopUpDate': 'cmComSeededLastTopUpDate',
};

const REASON_I18N: Record<string, string> = {
  out_of_range: 'admin.settings.errors.out_of_range',
  invalid_guid: 'admin.settings.errors.invalid_guid',
  invalid_phone: 'admin.settings.errors.invalid_phone',
  invalid_time: 'admin.settings.errors.invalid_time',
  invalid_date: 'admin.settings.errors.invalid_date',
  required: 'admin.settings.errors.required',
  too_many: 'admin.settings.errors.too_many',
  overlapping_target: 'admin.settings.passwordPolicies.errors.overlapping',
  invalid_target: 'admin.settings.passwordPolicies.errors.invalidTarget',
  duplicate_id: 'admin.settings.passwordPolicies.errors.duplicateId',
  invalid_id: 'admin.settings.passwordPolicies.errors.invalidId',
};

export function settingsFieldErrorMessage(reason: string): string {
  const key = REASON_I18N[reason];
  return key ? t(key) : t('admin.settings.errors.out_of_range');
}

export function isGuidLike(value: string): boolean {
  const normalized = value.trim().replace(/^\{|\}$/g, '');
  if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(normalized)) {
    return true;
  }
  return /^[0-9a-fA-F]{32}$/.test(normalized);
}

export function isE164Phone(value: string): boolean {
  return isValidE164Phone(value);
}

/** Client-side checks beyond HTML5 min/max/required. Keys are API field paths. */
export function validateSettingsFormClient(form: HTMLFormElement): Record<string, string> {
  const data = new FormData(form);
  const fields: Record<string, string> = {};

  const accountId = String(data.get('cmComLogicalAccountId') ?? '').trim();
  if (accountId && !isGuidLike(accountId)) {
    fields['cmComBudget.logicalAccountId'] = 'invalid_guid';
  }

  const phones = String(data.get('cmComOpsPhoneNumbers') ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (phones.length > 5) {
    fields['cmComBudget.opsPhoneNumbers'] = 'too_many';
  } else {
    for (const phone of phones) {
      if (!normalizeToE164(phone)) {
        fields['cmComBudget.opsPhoneNumbers'] = 'invalid_phone';
        break;
      }
    }
  }

  const seeded = String(data.get('cmComSeededLastTopUpDate') ?? '').trim();
  if (seeded && Number.isNaN(Date.parse(seeded))) {
    fields['cmComBudget.seededLastTopUpDate'] = 'invalid_date';
  }

  const enabled = data.get('cmComBudgetEnabled') === 'on';
  if (enabled) {
    if (!accountId) {
      fields['cmComBudget.logicalAccountId'] = 'required';
    }
    if (phones.length === 0 && !fields['cmComBudget.opsPhoneNumbers']) {
      fields['cmComBudget.opsPhoneNumbers'] = 'required';
    }
  }

  return fields;
}

export function clearSettingsFieldErrors(form: HTMLFormElement): void {
  for (const el of form.querySelectorAll('[aria-invalid="true"]')) {
    el.removeAttribute('aria-invalid');
  }
  for (const el of form.querySelectorAll('.admin-settings-field-error')) {
    el.remove();
  }
}

export function applySettingsFieldErrors(
  form: HTMLFormElement,
  fields: Record<string, string>,
): void {
  clearSettingsFieldErrors(form);

  let firstControl: HTMLElement | null = null;

  for (const [path, reason] of Object.entries(fields)) {
    const controlName = SETTINGS_FIELD_TO_CONTROL[path];
    if (!controlName) {
      continue;
    }

    const control = form.elements.namedItem(controlName);
    if (!(control instanceof HTMLElement)) {
      continue;
    }

    control.setAttribute('aria-invalid', 'true');

    const label = control.closest('label');
    const host = label ?? control.parentElement;
    if (host) {
      const existing = host.querySelector<HTMLElement>(
        `.admin-settings-field-error[data-field="${CSS.escape(controlName)}"]`,
      );
      const errorEl = existing ?? document.createElement('p');
      errorEl.className = 'error-text admin-settings-field-error';
      errorEl.dataset.field = controlName;
      errorEl.textContent = settingsFieldErrorMessage(reason);
      if (!existing) {
        host.appendChild(errorEl);
      }
    }

    if (!firstControl) {
      firstControl = control;
    }
  }

  firstControl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (firstControl instanceof HTMLElement && 'focus' in firstControl) {
    try {
      firstControl.focus({ preventScroll: true });
    } catch {
      // ignore focus failures
    }
  }
}
